/**
 * 物品配置编辑器组件
 * 提供物品配置的表单式编辑界面
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Switch,
  Typography,
  Divider,
  Alert,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import type { ItemConfigData, ItemEffect } from '@/types/scriptConfigBlock'

const { TextArea } = Input
const { Text } = Typography

// 物品类型选项
const ITEM_TYPE_OPTIONS = [
  { value: 'evidence', label: '证据' },
  { value: 'family_token', label: '信物' },
  { value: 'consumable', label: '消耗品' },
  { value: 'weapon', label: '武器' },
  { value: 'tool', label: '工具' },
  { value: 'ammo', label: '弹药' },
  { value: 'currency', label: '货币' },
  { value: 'clothing', label: '服装' },
  { value: 'key', label: '凭证' },
  { value: 'information', label: '信息' },
  { value: 'armor', label: '护甲' },
]

// 稀有度选项
const RARITY_OPTIONS = [
  { value: 'common', label: '普通', color: '#999' },
  { value: 'uncommon', label: '优秀', color: '#52c41a' },
  { value: 'rare', label: '稀有', color: '#1890ff' },
  { value: 'epic', label: '史诗', color: '#722ed1' },
  { value: 'legendary', label: '传说', color: '#faad14' },
]

// 物品编辑器属性
interface ItemEditorFormProps {
  initialData?: ItemConfigData
  onSave: (data: ItemConfigData) => Promise<void>
  loading?: boolean
}

// 效果编辑器
const EffectEditor: React.FC<{
  effect: ItemEffect
  index: number
  onUpdate: (index: number, effect: ItemEffect) => void
  onRemove: (index: number) => void
}> = ({ effect, index, onUpdate, onRemove }) => {
  return (
    <Row gutter={8} style={{ marginBottom: 8 }}>
      <Col span={4}>
        <Input
          value={effect.effect_type}
          onChange={(e) => onUpdate(index, { ...effect, effect_type: e.target.value })}
          placeholder="效果类型"
        />
      </Col>
      <Col span={4}>
        <Input
          value={effect.target || ''}
          onChange={(e) => onUpdate(index, { ...effect, target: e.target.value })}
          placeholder="目标"
        />
      </Col>
      <Col span={4}>
        <Input
          value={effect.value !== undefined ? String(effect.value) : ''}
          onChange={(e) => onUpdate(index, { ...effect, value: e.target.value })}
          placeholder="数值"
        />
      </Col>
      <Col span={4}>
        <InputNumber
          value={effect.duration}
          onChange={(value) => onUpdate(index, { ...effect, duration: value || undefined })}
          min={0}
          placeholder="持续"
          style={{ width: '100%' }}
        />
      </Col>
      <Col span={6}>
        <Input
          value={effect.description || ''}
          onChange={(e) => onUpdate(index, { ...effect, description: e.target.value })}
          placeholder="描述"
        />
      </Col>
      <Col span={2}>
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
      </Col>
    </Row>
  )
}

// 主组件
const ItemEditorForm: React.FC<ItemEditorFormProps> = ({
  initialData,
  onSave,
  loading = false
}) => {
  const [form] = Form.useForm()
  const [effects, setEffects] = useState<ItemEffect[]>([])
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      // 处理 properties 对象：转换为 JSON 字符串
      const normalizeProperties = (properties: any): string => {
        if (!properties) return '{}'
        if (typeof properties === 'object') {
          return JSON.stringify(properties, null, 2)
        }
        return String(properties)
      }

      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description,
        item_type: initialData.item_type,
        rarity: initialData.rarity,
        icon: initialData.icon,
        value: initialData.value,
        weight: initialData.weight,
        max_stack: initialData.max_stack,
        can_trade: initialData.can_trade,
        can_drop: initialData.can_drop,
        properties: normalizeProperties(initialData.properties),
      })
      setEffects(initialData.effects || [])
    } else {
      form.resetFields()
      setEffects([])
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      // 解析 properties JSON 字符串
      const parseJsonField = (value: any, defaultValue: any = {}): any => {
        if (!value) return defaultValue
        if (typeof value === 'string' && value.trim().startsWith('{')) {
          try {
            return JSON.parse(value)
          } catch {
            return defaultValue
          }
        }
        return value
      }

      const itemData: ItemConfigData = {
        key: values.key,
        name: values.name,
        description: values.description,
        item_type: values.item_type,
        rarity: values.rarity,
        icon: values.icon,
        value: values.value,
        weight: values.weight,
        max_stack: values.max_stack,
        can_trade: values.can_trade,
        can_drop: values.can_drop,
        effects,
        properties: parseJsonField(values.properties, {}),
      }

      await onSave(itemData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  // 效果操作
  const addEffect = () => {
    setEffects([
      ...effects,
      { effect_type: '', value: 0 }
    ])
  }

  const updateEffect = (index: number, effect: ItemEffect) => {
    const newEffects = [...effects]
    newEffects[index] = effect
    setEffects(newEffects)
  }

  const removeEffect = (index: number) => {
    setEffects(effects.filter((_, i) => i !== index))
  }

  // 根据物品类型显示不同配置
  const itemType = Form.useWatch('item_type', form)
  const showWeaponConfig = itemType === 'weapon'
  const showConsumableConfig = itemType === 'consumable'

  return (
    <Card
      title={
        <Space>
          <ShoppingCartOutlined style={{ color: '#52c41a' }} />
          <span>{initialData?.name || '新建物品'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
          {itemType && <Tag>{itemType}</Tag>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="物品键" rules={[{ required: true }]}>
                  <Input placeholder="例如: item_evidence_document" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="物品名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 机密文件" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="item_type" label="物品类型" rules={[{ required: true }]}>
                  <Select placeholder="选择物品类型" options={ITEM_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={16}>
                <Form.Item name="description" label="描述">
                  <TextArea rows={2} placeholder="物品的详细描述..." />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="icon" label="图标引用">
                  <Input placeholder="icons/items/..." />
                </Form.Item>
              </Col>
            </Row>

            <Divider>数值属性</Divider>

            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="value" label="价值">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="weight" label="重量">
                  <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="0.1" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="max_stack" label="最大堆叠">
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="rarity" label="稀有度">
                  <Select
                    placeholder="选择稀有度"
                    options={RARITY_OPTIONS.map(r => ({
                      value: r.value,
                      label: <Space><Tag color={r.color}>{r.label}</Tag></Space>
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="can_trade" label="可交易" valuePropName="checked">
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="can_drop" label="可丢弃" valuePropName="checked">
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab={`效果 (${effects.length})`} key="effects">
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={addEffect}>
                添加效果
              </Button>
            </div>

            <Row gutter={8} style={{ marginBottom: 8 }}>
              <Col span={4}><Text strong>效果类型</Text></Col>
              <Col span={4}><Text strong>目标</Text></Col>
              <Col span={4}><Text strong>数值</Text></Col>
              <Col span={4}><Text strong>持续时间</Text></Col>
              <Col span={6}><Text strong>描述</Text></Col>
              <Col span={2}></Col>
            </Row>

            {effects.map((effect, index) => (
              <EffectEditor
                key={index}
                effect={effect}
                index={index}
                onUpdate={updateEffect}
                onRemove={removeEffect}
              />
            ))}

            {effects.length === 0 && (
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text type="secondary">暂无效果配置，点击上方按钮添加</Text>
              </Card>
            )}
          </TabPane>

          {showWeaponConfig && (
            <TabPane tab="武器属性" key="weapon">
              <Alert
                message="武器配置说明"
                description="武器类型的物品需要配置战斗属性，包括伤害、射程、弹药等。"
                type="info"
                style={{ marginBottom: 16 }}
              />
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name={['combat_stats', 'damage']} label="伤害">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['combat_stats', 'range']} label="射程">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['combat_stats', 'stealth_bonus']} label="潜行加成">
                    <InputNumber style={{ width: '100%' }} placeholder="0" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name={['combat_stats', 'ammo_required']} label="需要弹药" valuePropName="checked">
                    <Switch checkedChildren="是" unCheckedChildren="否" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['combat_stats', 'ammo_type']} label="弹药类型">
                    <Input placeholder="bullet" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['equipment_slot']} label="装备槽位">
                    <Select
                      placeholder="选择装备槽位"
                      options={[
                        { value: 'weapon', label: '武器' },
                        { value: 'clothing', label: '服装' },
                        { value: 'armor', label: '护甲' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
          )}

          {showConsumableConfig && (
            <TabPane tab="消耗品属性" key="consumable">
              <Alert
                message="消耗品配置说明"
                description="消耗品类型的物品使用效果字段来定义恢复效果，例如 health: 30 表示恢复30点生命值。"
                type="info"
                style={{ marginBottom: 16 }}
              />
              <Card size="small">
                <Text type="secondary">
                  消耗品效果应在「效果」标签页中添加，常用效果类型包括：
                </Text>
                <ul style={{ marginTop: 8 }}>
                  <li><Text code>health</Text> - 恢复生命值</li>
                  <li><Text code>food</Text> - 恢复饥饿/饱食度</li>
                  <li><Text code>water</Text> - 恢复水分</li>
                  <li><Text code>heal_amount</Text> - 治疗量</li>
                </ul>
              </Card>
            </TabPane>
          )}

          <TabPane tab="高级属性" key="advanced">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="properties" label="附加属性 (JSON)">
                  <TextArea
                    rows={6}
                    placeholder='{"unique": true, "consumable": true}'
                    style={{ fontFamily: 'monospace' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="源设置">
                  <Select
                    mode="tags"
                    placeholder="物品来源 NPC 或地点"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs

export default ItemEditorForm
